import { ContactId, FieldSet } from '@api';
import classnames from 'classnames';
import * as React from 'react';
import { DecoratedFormProps, reduxForm } from 'redux-form';
import { InjectedFormProps } from 'redux-form/lib/reduxForm';
import { sendAdobeAnalyticsEvent } from 'src/js/actions/AdobeAnalyticActions';
import { AdobeAnalyticsFormAbandonTrackService, sendLoginEvent } from 'src/js/services/AdobeAnalyticsService';
import { showFormValidation, showSyncErrors } from '../../actions/Actions';
import { Phase } from '../../reducers/State';
import CheckoutService from '../../services/CheckoutService';
import { CONTACT_ADD_FORM_NAME, Values } from './actions/Actions';
import { validateContact } from './actions/Validations';
import { ContactAdd } from './components/ContactAdd';

interface Props {
  childName?: string,
  phase?: number,
  isFirst?: boolean,
  isEmptyProducts?: boolean,
  isOnlyWaitingCoursesInCart?: boolean,
  onSuccess?: any,
  onCancel?: any,
  fetching?: boolean,
  fieldset?: FieldSet,
  minAge?: number
}

class ContactAddForm extends React.Component<InjectedFormProps<Values,Props> & DecoratedFormProps<Values,Props>, any> {
  componentDidMount() {
    AdobeAnalyticsFormAbandonTrackService.attachFormAbandonClicksTrack(this.props.form, this.props.dispatch)
  }

  componentWillUnmount() {
    AdobeAnalyticsFormAbandonTrackService.detachFormAbandonClicksTrack()
  }

  getMessages() {
    const {childName, minAge = 18, phase, isFirst, isEmptyProducts, isOnlyWaitingCoursesInCart} = this.props;

    const addStudentMessage = isEmptyProducts ?
    {
      title: 'Add a student',
      message: isOnlyWaitingCoursesInCart ? 'Enter the person who is interested in the class.' : 'Enter the person who will attend the class',
      className: "add-student"
    } :
    {
      title: 'Add a person',
      message: 'Enter the person who will attend the class or make a purchase.',
      className: "add-person"
    };

    const addPayerMessage = {
      title: 'Add a payer',
      message: 'Enter the person to be invoiced and paying for this transaction.',
      className: "add-payer"
    };

    switch (phase) {
      case Phase.ChangeParent:
        return {
          title: `Change a parent or guardian for ${childName}`,
          message: 'Enter the person who will attend the class or make a purchase.',
          className: "change-parent"
        };

      case Phase.AddPayer:
        return isFirst ? addStudentMessage : addPayerMessage;

      case Phase.AddContactAsPayer:
        return {
          title: 'Add a payer',
          message: 'Enter the person to be invoiced and paying for this transaction.',
          className: "add-payer"
        };

      case Phase.AddParent:
        return {
          title: 'Add a parent or guardian',
          message: `Because a student is under ${minAge} we require the details of 
                  a parent or guardian for our records. Please enter that person.`,
          className: "add-parent"
        };

      default:
        return addStudentMessage;
    }
  }

  onSubmitPress = (values) => {
      const { fieldset } = this.props;
      return CheckoutService.createOrGetContact(values, fieldset)
  }

  render() {
    const {handleSubmit, onCancel, pristine, submitting, fetching} = this.props;

    return (
      <div className={`contact-editor-form contact-${this.getMessages()?.className}`}>
        <h2>{this.getMessages().title}</h2>
        <form
          onSubmit={handleSubmit(this.onSubmitPress)}
          id="contactEditorForm"
          className={classnames({submitting: submitting || fetching})}
        >
          <ContactAdd header={this.getMessages().message}/>
          <div className="form-controls flex">
            <span>
              {onCancel &&
              <a
                href="#"
                onClick={onCancel}
              > Cancel
              </a>
              }
            </span>

            <input
              value="OK"
              className="btn btn-primary"
              name="submitContact"
              type="submit"
              disabled={pristine || submitting}
            />
          </div>
        </form>
      </div>
    );
  }
}

const Form = reduxForm<Values,Props>({
  form: CONTACT_ADD_FORM_NAME,
  validate: validateContact,
  destroyOnUnmount: true,
  onSubmitSuccess: (result: ContactId, dispatch, props) => {
    dispatch(props.onSuccess(result, props.values));
    dispatch(sendAdobeAnalyticsEvent('your details – step 1'));
    sendLoginEvent(
      result.newContact ? 'createAccountSuccessful' : 'loginSuccess',
      props.values.email,
      result.id
    )
  },
  onSubmitFail: (errors, dispatch, submitError, props) => {
    if (errors && !submitError) {
      dispatch(showSyncErrors(errors));
    } else {
      dispatch(showFormValidation(submitError, CONTACT_ADD_FORM_NAME));
    }
    sendLoginEvent(
      'loginFail',
      props.values.email,
      'NA'
    )
  },
})(ContactAddForm);


export default Form;
